import React from "react";
import { useTranslation } from "react-i18next";
import { mutate } from "swr";
import {
    usePagination,
    useTable,
    useGlobalFilter,
    useRowSelect,
} from "react-table";
import BTable from "react-bootstrap/Table";
import swal from "@sweetalert/with-react";
import NProgress from "nprogress";
import { useSnapshot } from "valtio";
import {
    StyledButton,
    StyledIconButton,
    StyledSearchInput,
    TrashIcon,
    EditIcon,
    AngleSmallLeftIcon,
} from "@aureskonnect/react-ui";

import { store as vStore } from "@vStore";
import store, { setGroupedSubFamilies } from "./store";

import { useResource } from "@hooks/useResource";

import { ITEMS_PER_PAGE } from "@constants/index";

import CustomErrorBoundary from "@components/Common/CustomErrorBoundary";
import ErrorPage from "@components/Common/ErrorPage";
import { NoImagePlaceholderIcon } from "../components/NoImagePlaceholderIcon";
import Pagination from "@components/Common/StatisticsPagination";
import { ModalWrapper } from "./ModalWrapper";

export default function EditStep({ go }: any) {
    const { t } = useTranslation();

    const { mainColor } = useSnapshot(vStore);
    const { groupName } = useSnapshot(store);

    const [
        selectedSubFamiliesList,
        setSelectedSubFamiliesList,
    ] = React.useState<any[]>([]);
    const [newGroupDesignation, setNewGroupDesignation] = React.useState<
        string
    >(groupName);
    const [isModalOpened, setIsModalOpened] = React.useState<boolean>(false);

    const [isGroupNameEmpty, setIsGroupNameEmpty] = React.useState<boolean>(
        false
    );

    const [
        isNameExistsInGroupsList,
        setIsNameExistsInGroupsList,
    ] = React.useState<boolean>(false);

    const { resourceData, error, mutate: mutateGroupDetails } = useResource(
        `${process.env.REACT_APP_API_V1_URL}/settings/sub-families/group-detail?group-name=${groupName}`
    );

    React.useEffect(() => {
        setSelectedSubFamiliesList(resourceData.data);
    }, [resourceData]);

    const handleRemoveSelectedSubFamily = React.useCallback(
        async (subFamily: any) => {
            swal({
                text: t("Are you sure you want to delete this group?"),
                icon: "warning",
                buttons: [t("Cancel"), t("Delete")],
                dangerMode: true,
            }).then(async (willDelete: boolean) => {
                if (willDelete) {
                    let localSelectedSubFamiliesList = selectedSubFamiliesList
                        .map((s: any) => `${s.sub_family}|${s.family}`)
                        .filter((item: string) => {
                            return !item.includes(
                                `${subFamily.sub_family}|${subFamily.family}`
                            );
                        });

                    let apiUrl = `${process.env.REACT_APP_API_V1_URL}/settings/sub-families/groups`;

                    try {
                        NProgress.start();

                        mutate(
                            apiUrl,
                            await fetch(apiUrl, {
                                headers: {
                                    "Content-Type": "application/json",
                                    authorization: `Bearer ${localStorage.getItem(
                                        "jwt"
                                    )}`,
                                },
                                body: JSON.stringify({
                                    designation: newGroupDesignation,
                                    groupment: localSelectedSubFamiliesList,
                                }),
                                method: "PUT",
                            })
                                .then((response) => response.json())
                                .then((data) => {
                                    NProgress.done();

                                    if (data.error) {
                                        throw Error(
                                            "Error while saving selection!"
                                        );
                                    }

                                    setSelectedSubFamiliesList(
                                        (prevState: any) => {
                                            let newState = prevState.filter(
                                                (item: any) => {
                                                    return (
                                                        `${item.sub_family}|${item.family}` !==
                                                        `${subFamily.sub_family}|${subFamily.family}`
                                                    );
                                                }
                                            );

                                            mutateGroupDetails({
                                                error: false,
                                                data: newState,
                                            });

                                            return newState;
                                        }
                                    );

                                    return swal({
                                        icon: "success",
                                        content: <p>{t(data.message)}!</p>,
                                        buttons: false,
                                        timer: 2000,
                                    });
                                })
                        );
                    } catch (e: any) {
                        NProgress.done();

                        return swal({
                            icon: "error",
                            content: <p>{t("There's an error")!}</p>,
                            buttons: false,
                            timer: 2000,
                        });
                    }
                }
            });
        },
        [t, newGroupDesignation, selectedSubFamiliesList, mutateGroupDetails]
    );

    const columns = React.useMemo(
        () => [
            {
                Header: t("Subfamily"),
                accessor: "sub_family",
            },
            {
                Header: t("Picture"),
                accessor: function (subFamily: any) {
                    return (
                        <div>
                            {subFamily.image === "" ? (
                                <NoImagePlaceholderIcon />
                            ) : (
                                subFamily.image
                            )}
                        </div>
                    );
                },
                disableGlobalFilter: true,
            },
            {
                Header: t("Family"),
                accessor: "family",
            },
            {
                Header: "",
                id: "actions",
                disableGlobalFilter: true,
                accessor: function (subFamily: any) {
                    return (
                        <div>
                            <StyledButton
                                rounded={true}
                                outline={true}
                                variant={mainColor === null ? "secondary" : ""}
                                className="d-none d-sm-none d-md-block white-space-nowrap"
                                style={{
                                    color: mainColor,
                                    borderColor: mainColor,
                                    float: "right",
                                }}
                                onClick={() =>
                                    handleRemoveSelectedSubFamily(subFamily)
                                }
                            >
                                {t("Delete")}
                            </StyledButton>
                            <StyledIconButton
                                className="d-block d-sm-block d-md-none m-0"
                                icon="TrashIcon"
                                onClick={() =>
                                    handleRemoveSelectedSubFamily(subFamily)
                                }
                            >
                                <TrashIcon height={20} width={20} />
                            </StyledIconButton>
                        </div>
                    );
                },
            },
        ],
        [t, handleRemoveSelectedSubFamily, mainColor]
    );

    const IndeterminateCheckbox = React.forwardRef(
        ({ indeterminate, ...rest }: any, ref: any) => {
            const defaultRef = React.useRef();
            const resolvedRef = ref || defaultRef;

            React.useEffect(() => {
                resolvedRef.current!.indeterminate = indeterminate;
            }, [resolvedRef, indeterminate]);

            let localProps = { ...rest, title: t("Select line") };

            return <input type="checkbox" ref={resolvedRef} {...localProps} />;
        }
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex },
        setGlobalFilter,
        selectedFlatRows,
    } = useTable(
        {
            columns,
            data: selectedSubFamiliesList,
            initialState: { pageIndex: 0 },
        },
        useGlobalFilter,
        usePagination,
        useRowSelect,
        (hooks) => {
            hooks.visibleColumns.push((columns) => [
                {
                    id: "selection",
                    Header: ({ getToggleAllPageRowsSelectedProps }) => (
                        <IndeterminateCheckbox
                            {...getToggleAllPageRowsSelectedProps()}
                        />
                    ),
                    Cell: ({ row }) => (
                        <IndeterminateCheckbox
                            {...row.getToggleRowSelectedProps()}
                        />
                    ),
                    disableGlobalFilter: true,
                },
                ...columns,
            ]);
        }
    );

    async function handleMultipleRemoveFamiliesButtonOnClickEvent() {
        swal({
            text: t("Are you sure you want to delete this group?"),
            icon: "warning",
            buttons: [t("Cancel"), t("Delete")],
            dangerMode: true,
        }).then(async (willDelete: boolean) => {
            if (willDelete) {
                let localSelectedSubFamilies: any[] = selectedFlatRows
                    .map((row: any) => row.original)
                    .map((s: any) => `${s.sub_family}|${s.family}`);

                let subFamilies: any[] = [];

                selectedSubFamiliesList.forEach((subFamily: any) => {
                    if (
                        !localSelectedSubFamilies.includes(
                            `${subFamily.sub_family}|${subFamily.family}`
                        )
                    ) {
                        subFamilies.push(
                            `${subFamily.sub_family}|${subFamily.family}`
                        );
                    }
                });

                let apiUrl = `${process.env.REACT_APP_API_V1_URL}/settings/sub-families/groups`;

                try {
                    mutate(
                        apiUrl,
                        await fetch(apiUrl, {
                            headers: {
                                "Content-Type": "application/json",
                                authorization: `Bearer ${localStorage.getItem(
                                    "jwt"
                                )}`,
                            },
                            body: JSON.stringify({
                                designation: newGroupDesignation,
                                groupment: subFamilies,
                            }),
                            method: "PUT",
                        })
                            .then((response) => response.json())
                            .then(async (data) => {
                                if (data.error) {
                                    throw Error(
                                        "Error while saving selection!"
                                    );
                                }

                                setSelectedSubFamiliesList((prevState: any) => {
                                    let newState = prevState.filter(
                                        (item: any) => {
                                            return subFamilies.includes(
                                                `${item.sub_family}|${item.family}`
                                            );
                                        }
                                    );

                                    mutateGroupDetails({
                                        error: false,
                                        data: newState,
                                    });

                                    return newState;
                                });

                                return swal({
                                    icon: "success",
                                    content: <p>{t(data.message)}!</p>,
                                    buttons: false,
                                    timer: 2000,
                                });
                            })
                    );
                } catch (e: any) {
                    return swal({
                        icon: "error",
                        content: <p>{t("There's an error")!}</p>,
                        buttons: false,
                        timer: 2000,
                    });
                }
            }
        });
    }

    function handlePreviousStepAction() {
        go(0);
    }

    function toggleOpeningModal() {
        setIsGroupNameEmpty(false);
        setIsModalOpened((prevState) => !prevState);
        setIsNameExistsInGroupsList(false);
    }

    React.useEffect(() => {
        setPageSize(ITEMS_PER_PAGE);
    }, [setPageSize]);

    function handleAddingNewSubFamiliesToSubFamiliesGroup() {
        setGroupedSubFamilies(selectedSubFamiliesList);
        go(4);
    }

    return (
        <React.Fragment>
            {error ? (
                <ErrorPage />
            ) : (
                <CustomErrorBoundary>
                    <div className="d-md-flex justify-content-md-between align-items-md-center">
                        <div className="d-md-flex align-items-md-center mt-2 mt-md-2 mb-2">
                            <div className="d-flex align-items-center">
                                <StyledIconButton
                                    icon="AngleSmallLeftIcon"
                                    className="m-0 p-0"
                                    onClick={handlePreviousStepAction}
                                >
                                    <AngleSmallLeftIcon
                                        height={35}
                                        width={35}
                                    />
                                </StyledIconButton>
                                <span className="mx-2 mb-0 font-size-14 white-space-nowrap h5">
                                    {newGroupDesignation}
                                </span>
                                <StyledIconButton
                                    icon="EditIcon"
                                    className="m-0"
                                    onClick={toggleOpeningModal}
                                >
                                    <EditIcon height={20} width={20} />
                                </StyledIconButton>
                            </div>
                            <span
                                className="divider mt-0 mx-4 d-none d-md-block"
                                style={{
                                    backgroundColor: "#808080a6",
                                    height: "2.25rem",
                                    width: "2px",
                                }}
                            />
                            <StyledSearchInput
                                onChange={(e) => {
                                    setGlobalFilter(e.target.value);
                                }}
                                placeholder={`${t("Search")} ...`}
                                className="p-0 mx-2 mx-md-0 mt-2 mt-md-0"
                            />
                        </div>
                        <div className="d-md-flex mt-2 mx-2 mb-2">
                            <StyledButton
                                rounded={true}
                                variant={mainColor === null ? "secondary" : ""}
                                className="mr-2 mb-2 mb-md-0 w-xs-100 w-sm-100 w-md-unset white-space-nowrap white-space-nowrap"
                                style={{
                                    background: mainColor,
                                }}
                                disabled={selectedFlatRows.length === 0}
                                onClick={
                                    handleMultipleRemoveFamiliesButtonOnClickEvent
                                }
                            >
                                {t("Delete sub-families")}
                            </StyledButton>
                            <StyledButton
                                rounded={true}
                                variant={mainColor === null ? "secondary" : ""}
                                className="mr-2 w-xs-100 w-sm-100 w-md-unset white-space-nowrap"
                                style={{
                                    background: mainColor,
                                }}
                                onClick={
                                    handleAddingNewSubFamiliesToSubFamiliesGroup
                                }
                            >
                                {t("Add sub-families")}
                            </StyledButton>
                        </div>
                    </div>
                    {page.length > 0 ? (
                        <React.Fragment>
                            <BTable responsive {...getTableProps()}>
                                <thead>
                                    {headerGroups.map((headerGroup) => (
                                        <tr
                                            {...headerGroup.getHeaderGroupProps()}
                                        >
                                            {headerGroup.headers.map(
                                                (column) => (
                                                    <th
                                                        className="white-space-nowrap"
                                                        {...column.getHeaderProps()}
                                                    >
                                                        {column.render(
                                                            "Header"
                                                        )}
                                                    </th>
                                                )
                                            )}
                                        </tr>
                                    ))}
                                </thead>
                                <tbody {...getTableBodyProps()}>
                                    {page.map((row, i) => {
                                        prepareRow(row);
                                        return (
                                            <tr {...row.getRowProps()}>
                                                {row.cells.map((cell) => {
                                                    return (
                                                        <td
                                                            className="white-space-nowrap align-middle"
                                                            {...cell.getCellProps()}
                                                        >
                                                            {cell.render(
                                                                "Cell"
                                                            )}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </BTable>
                            {selectedSubFamiliesList.length > ITEMS_PER_PAGE ? (
                                <Pagination
                                    canPreviousPage={canPreviousPage}
                                    canNextPage={canNextPage}
                                    pageOptions={pageOptions}
                                    pageCount={pageCount}
                                    gotoPage={gotoPage}
                                    nextPage={nextPage}
                                    previousPage={previousPage}
                                    pageIndex={pageIndex}
                                />
                            ) : null}
                        </React.Fragment>
                    ) : null}
                    <ModalWrapper
                        isModalOpened={isModalOpened}
                        isGroupNameEmpty={isGroupNameEmpty}
                        isNameExistsInGroupsList={isNameExistsInGroupsList}
                        toggleOpeningModal={toggleOpeningModal}
                        setNewGroupDesignation={setNewGroupDesignation}
                        setIsGroupNameEmpty={setIsGroupNameEmpty}
                        setIsNameExistsInGroupsList={
                            setIsNameExistsInGroupsList
                        }
                    />
                </CustomErrorBoundary>
            )}
        </React.Fragment>
    );
}
